<template>
  <PForm
    ref="pForm"
    :heading="`${formState.edit ? 'Edit' : 'Create'} Purchase Order`"
    :primary-cta="submitBtn"
    @close="onClose"
    @reset="onReset"
    @submit.prevent="onSubmit"
  >
    <template #header-info>
      <PBtn
        v-if="!formState.edit"
        :fluid="false"
        class="q-mr-md"
        label="Magic Upload"
        outline
        @click="
          toggleUploadForm({
            open: true,
            props: { toggle: { method: onUploaded } },
          })
        "
      />
    </template>
    <fieldset class="border-none q-ma-none q-pa-none">
      <legend class="q-mb-sm text-neutral text-weight-medium text-md">
        Enter Purchase Order Details
      </legend>
      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PSelectInput
            v-model="formData.buyer"
            :emit-value="false"
            :map-options="false"
            :options="unref(buyerFilter.entities)"
            :options-loading="unref(buyerFilter.loading)"
            :validations="{
              label: 'Customer',
            }"
            label="Select Customer"
            :option-label="getBuyerOptionLabel"
            option-value="_id"
            placeholder="Select customer"
            required
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            @virtual-scroll="buyerFilter.onScroll"
            @filter="buyerFilter.onFilter"
            @filter-abort="buyerFilter.onFilterAbort"
            @update:model-value="onUpdateBuyer"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps">
                <q-item-section>
                  <q-item-label class="text-sm">
                    <span class="text-neutral">{{ opt.shop_name }}</span>
                  </q-item-label>
                  <q-item-label v-if="opt.gstin || opt.state" caption lines="1">
                    <span class="text-neutral-70 block">
                      GSTIN: {{ opt.gstin }}
                    </span>
                    <span class="text-neutral-70">State: {{ opt.state }}</span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>
        <div class="col-4">
          <PInput
            v-model.trim="formData.number"
            :required="isPONumberRequired"
            :rules="[validateNumber]"
            :loading="validatingNumber"
            :disable="!formData.buyer"
            :hide-bottom-space="!formData.buyer"
            label="PO Number"
            debounce="1000"
            placeholder="Enter PO number"
          />
          <p
            v-if="!formData.buyer"
            class="text-error-60 text-xs q-pt-xs q-px-sm q-mb-none text-weight-thin"
            :style="{ lineHeight: 1 }"
          >
            Please select buyer first
          </p>
        </div>
        <div class="col-4">
          <PSelectInput
            v-model="formData.delivery_type"
            label="Delivery Type"
            placeholder="Select type"
            :options="deliveryTypeOptions"
            required
          />
        </div>
        <div class="col-4">
          <PSelectInput
            v-model="computedPaymentTerms"
            :options="unref(paymentTermsFilter.entities)"
            label="Payment Terms"
            option-label="duration"
            option-value="duration"
            label-suffix="unit"
            placeholder="Select terms"
            required
            @filter="paymentTermsFilter.onFilter"
            @filter-abort="paymentTermsFilter.onFilterAbort"
          >
          </PSelectInput>
        </div>
        <div class="col-4">
          <PVDateInput
            v-model="formData.date"
            label="Issue Date"
            placeholder="DD-MM-YYYY"
            required
            :max-date="currentDate"
          />
        </div>
        <div class="col-4">
          <PVDateInput
            v-model="formData.expiry_date"
            label="Expiry Date"
            placeholder="DD-MM-YYYY"
          />
        </div>
        <div class="col-4">
          <PVDateInput
            v-model="formData.delivery_date"
            label="Expect. Delivery Date"
            placeholder="DD-MM-YYYY"
            required
          />
        </div>
        <div class="col-6 column q-col-gutter-xs">
          <PSelectInput
            v-model="unref(formData).billing_address"
            :cta="unref(formData).buyer ? addNewAddressButton('billing') : null"
            :disable="!unref(formData).buyer"
            :options="unref(billingAddressFilter.entities)"
            :options-loading="unref(billingAddressFilter.loading)"
            :option-label="
              (val) =>
                getAddressOptionLabel(val, unref(purchaseOrder).buyer?.gstin)
            "
            label="Bill To"
            required
            placeholder="Select an address"
            @virtual-scroll="billingAddressFilter.onScroll"
            @filter="billingAddressFilter.onFilter"
            @filter-abort="billingAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'billing_address',
                  unref(formData).buyer.gstin,
                  unref(formData).buyer?.shop_name,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id == unref(formData).billing_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(formData).buyer.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model="temp_billing_address"
            :disable="true"
            class="col"
            placeholder="Selected billing address"
            type="textarea"
            autogrow
            required
          />
        </div>
        <div class="col-6 column q-col-gutter-xs">
          <PSelectInput
            v-model="unref(formData).shipping_address"
            :cta="
              unref(formData).buyer ? addNewAddressButton('shipping') : null
            "
            :disable="!unref(formData).buyer"
            :options="unref(shippingAddressFilter.entities)"
            :options-loading="unref(shippingAddressFilter.loading)"
            :option-label="
              (val) =>
                getAddressOptionLabel(val, unref(purchaseOrder).buyer?.gstin)
            "
            label="Ship To"
            required
            placeholder="Select an address"
            @virtual-scroll="shippingAddressFilter.onScroll"
            @filter="shippingAddressFilter.onFilter"
            @filter-abort="shippingAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'shipping_address',
                  unref(formData).buyer?.gstin,
                  unref(formData).buyer?.shop_name,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id == unref(formData).shipping_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(formData).buyer.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model="temp_shipping_address"
            :disable="true"
            class="col"
            placeholder="Selected shipping address"
            type="textarea"
            required
            autogrow
          />
        </div>
      </div>
    </fieldset>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="overflow-hidden">
      <div class="q-mb-sm flex justify-between items-center">
        <legend class="text-neutral text-weight-medium text-md">Items</legend>
        <div class="flex items-center">
          <span class="q-mr-lg">Add Item from RFQ</span>
          <PSelectInput
            v-model="formData.rfq"
            :emit-value="false"
            :map-options="false"
            :options="unref(rfqFilter.entities)"
            :options-loading="unref(rfqFilter.loading)"
            option-label="number"
            option-value="_id"
            :disable="!formData.buyer"
            :hide-bottom-space="!formData.buyer"
            placeholder="Select an option"
            clearable
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            @virtual-scroll="rfqFilter.onScroll"
            @filter="rfqFilter.onFilter"
            @filter-abort="rfqFilter.onFilterAbort"
            @update:model-value="onUpdateRfq"
          />
        </div>
      </div>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :pagination="pagination"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :columns="productListColumns"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        table-header-class="text-neutral-70 bg-neutral-10"
        dense
        flat
        wrap-cells
      >
        <template #body-cell="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable v-model="props.row[props.col.name]" disable />
          </q-td>
        </template>

        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <ItemNameCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-qty="props">
          <q-td :key="props.col.name" :props="props">
            <ItemQuantityCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-rate="props">
          <q-td :key="props.col.name" :props="props">
            <ItemRateCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-amount="props">
          <q-td :key="props.col.name" :props="props">
            <ItemAmountCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-action="props">
          <q-td :props="props">
            <div class="flex no-wrap justify-center">
              <PBtn
                color="primary"
                dense
                flat
                round
                title="Edit Item"
                @click="
                  editItem({
                    item: props.row,
                    index: props.rowIndex,
                    entityType: 'Purchase Order',
                  })
                "
              >
                <EditIcon class="icon--sm" />
              </PBtn>
              <PBtn
                color="primary"
                dense
                flat
                round
                title="Delete Item"
                @click="removeProduct(props.rowIndex)"
              >
                <RemoveIcon class="icon--sm" />
              </PBtn>
            </div>
          </q-td>
        </template>
      </PTable>
      <p v-else class="text-neutral-50 q-mb-none">
        <span class="block q-mb-xs">No Items Added</span>
        <span class="text-error-60">
          <q-icon class="icon--sm" name="sym_r_error_outline" />
          At least one item is required.
        </span>
      </p>
      <span
        v-if="hasItemsWithZeroQtyOrRate"
        class="block q-mt-sm text-error-60"
      >
        <q-icon class="icon--sm" name="sym_r_error_outline" />
        Every item should have qty, rate and total greater than 0.
      </span>
      <PBtn
        :disable="formData.rfq != null"
        class="q-mt-md"
        outline
        @click="addItem({ entityType: 'Purchase Order' })"
      >
        <AddIcon class="icon--sm q-mr-sm" />
        <span>Add Items to PO</span>
      </PBtn>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-5 offset-7">
          <div class="row q-col-gutter-lg">
            <div class="col-6">
              <span class="text-neutral">Tax Total</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-5 offset-7">
          <div class="row q-col-gutter-lg">
            <div class="col-6">
              <p class="text-neutral q-mb-none">Grand Total</p>
              <span class="text-xs text-neutral-70">Includes Tax</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-md" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PFile
            v-if="!formState.edit"
            v-model="file"
            :required="isPOFileMandatory"
            label="Attach Proof of Document (Purchase Order)"
            help="Allowed file types are PDFs, images, excel, csv"
          />
          <PFile
            v-if="file"
            v-model="file"
            class="q-mt-xxs"
            variant="re-upload"
          />
          <FilesList
            v-if="formState.edit"
            :entity="formData"
            file-type="Purchase Order"
          />
        </div>
        <div class="col-6">
          <PSelectInput
            v-model="formData.owner"
            :emit-value="false"
            :map-options="false"
            :options="unref(ownerFilter.entities)"
            :options-loading="unref(ownerFilter.loading)"
            label="Account Owner"
            option-label="name"
            option-value="_id"
            placeholder="Select an owner"
            required
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            @virtual-scroll="ownerFilter.onScroll"
            @filter="ownerFilter.onFilter"
            @filter-abort="ownerFilter.onFilterAbort"
            @update:model-value="populateOwnerData"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps" :disable="!opt.is_active">
                <q-item-section>
                  <q-item-label class="text-sm">
                    <span class="text-neutral">{{ opt.name }}</span>
                  </q-item-label>
                  <q-item-label v-if="opt.email" caption lines="1">
                    <span class="text-neutral-70">{{ opt.email }}</span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>
        <div class="col-6">
          <PSelectInput
            v-model="formData.associated_agent"
            :emit-value="false"
            :map-options="false"
            :options="unref(agentFilter.entities)"
            :options-loading="unref(agentFilter.loading)"
            label="Associate Agent"
            option-label="name"
            option-value="_id"
            placeholder="Select an agent"
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            @virtual-scroll="agentFilter.onScroll"
            @filter="agentFilter.onFilter"
            @filter-abort="agentFilter.onFilterAbort"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps" :disable="!opt.is_active">
                <q-item-section>
                  <q-item-label class="text-sm">
                    <span class="text-neutral">{{ opt.name }}</span>
                  </q-item-label>
                  <q-item-label v-if="opt.email" caption lines="1">
                    <span class="text-neutral-70">{{ opt.email }}</span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>
      </div>
    </div>
    <section v-if="activeBills">
      <p class="text-warning-70">
        There are active purchase bills against this purchase order. Cancel
        those purchase bill before updating this purchase order.
      </p>
      <p class="text-weight-bold">Active Purchase Bills - {{ activeBills }}</p>
    </section>

    <div class="q-mt-sm text-weight-bold">
      {{ FORM_INFO }}
    </div>
  </PForm>
</template>

<script setup>
import { date } from "quasar";

import AddIcon from "assets/icons/add.svg";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";
import purchaseOrderFormModel from "src/modules/purchase-order/models/purchaseOrderForm";
import { useProductList } from "src/modules/purchase-order/composables/product-list";
import { numberFormat } from "assets/scripts/utilities/helpers";
import { FORM_INFO } from "assets/scripts/utilities/constants";
import { isValidDateString } from "assets/scripts/utilities/customValidators";
import { ref, unref } from "vue";
import { notify } from "assets/scripts/utilities/api-clients";
import * as addressApi from "src/api/address";

const { extractDate } = date;

const pForm = ref(null);
const currentDate = new Date();
const validatingNumber = ref(false);
const isDuplicatePO = ref(false);
const temp_billing_address = ref("");
const temp_shipping_address = ref("");
const activeBills = computed(() => {
  return unref(formData)
    ?.bills?.reduce((activeBills, bill) => {
      if (bill.status_cd === "active" && bill.number) {
        activeBills.push(bill.number);
      }

      return activeBills;
    }, [])
    ?.join(",");
});

const { formState, purchaseOrder } = storeToRefs(usePurchaseOrderStore());
const {
  isDuplicate,

  getCounts,
  getPurchaseOrders,
  getPurchaseOrderFiles,
  postPurchaseOrder,
  putPurchaseOrder,
  togglePurchaseOrderForm,
} = usePurchaseOrderStore();

const { toggleAddAddressForm } = useAddressStore();

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: purchaseOrderFormModel(),
  entity: purchaseOrder,
  formRef: pForm,
  isForm: formState,
  post: postPurchaseOrder,
  put: putPurchaseOrder,
  toggleForm: togglePurchaseOrderForm,
});

const deliveryTypeOptions = ["FOR", "Pickup"];

const buyerFilter = useFilter({
  entity: "user",
  props: ["gstin", "shop_name"],
  options: {
    params: {
      roles: "buyer",
      isActive: 1,
      isOperational: 1,
    },
  },
});

const getBuyerOptionLabel = (opt) => {
  const { shop_name, gstin } = opt;

  return `${shop_name} ${gstin ? "| " + gstin : ""}`;
};

const populateBuyerData = (buyer) => {
  if (!buyer) {
    return;
  }

  if (!unref(formState).edit) {
    formData.value.number = "";
  }

  formData.value.payment_terms = buyer?.credit_cycle;

  formData.value.shop_name = buyer?.shop_name;

  if (buyer?.account_owner) {
    formData.value.owner = buyer.account_owner;

    populateOwnerData(buyer.account_owner);
  } else {
    formData.value.owner = null;

    formData.value.owner_name = "";
  }
};

let billingAddressFilter = ref({});
let billingAddresses = ref([]);
let shippingAddressFilter = ref({});
let shippingAddresses = ref([]);

const populateBuyerAddressDropdown = async () => {
  const buyerId = unref(formData).buyer?._id;

  if (buyerId) {
    const billingAddressResponse = await addressApi.index({
      params: {
        get_primary: true,
        type: "billing",
        user_id: buyerId,
      },
    });

    billingAddresses.value = billingAddressResponse.data?.data;

    billingAddressFilter.value = useFilter({
      entities: billingAddresses,
      entity: "address",
    });

    const shippingAddressResponse = await addressApi.index({
      params: {
        get_primary: true,
        type: "shipping",
        user_id: buyerId,
      },
    });

    shippingAddresses.value = shippingAddressResponse.data?.data;

    shippingAddressFilter.value = useFilter({
      entities: shippingAddresses,
      entity: "address",
    });
  }
};

const onUpdateBuyer = (buyer) => {
  unref(formData).associated_agent = null;

  clearAddresses();

  populateBuyerData(buyer);

  populateBuyerAddressDropdown();

  initRfqFilter();
};

const clearAddresses = () => {
  unref(formData).billing_address = "";

  unref(formData).shipping_address = "";

  unref(formData).billing_address_info = {};

  unref(formData).shipping_address_info = {};

  clearTempAddresses();
};

let rfqFilter = {};

const initRfqFilter = () => {
  unref(formData).rfq = null;

  rfqFilter = useFilter({
    entity: "rfq",
    options: {
      params: {
        buyer: unref(formData).buyer._id,
      },
    },
  });
};

const onUpdateRfq = (rfq) => {
  unref(formData).lineitems =
    rfq?.lineitems?.map((item) => {
      if (item._id) {
        if (!item.item_id) {
          item.item_id = item._id;
        }

        delete item._id;
      }

      return item;
    }) || [];

  nextTick(() => {
    calculateTotalTax();
  });
};

const addNewAddress = (addressType) => {
  toggleAddAddressForm({
    open: true,
    level: 0,
    props: {
      documentType: "Purchase Order",
      userId: unref(formData).buyer._id,
      addressType: addressType,
      updateParent: selectAddress,
    },
  });
};

const selectAddress = (newAddressData) => {
  if (!newAddressData) {
    return;
  }

  if (newAddressData.type === "billing") {
    updateAddress(
      newAddressData,
      "billing_address",
      unref(formData).buyer?.gstin,
      unref(formData).buyer?.shop_name,
    );
  } else if (newAddressData.type === "shipping") {
    updateAddress(
      newAddressData,
      "shipping_address",
      unref(formData).buyer?.gstin,
      unref(formData).buyer?.shop_name,
    );
  }
};

const addNewAddressButton = (addressType) => {
  return {
    dense: true,
    flat: true,
    fluid: true,
    text: true,
    icon: "sym_r_add",
    label: `Add New Address`,
    onClick: () => addNewAddress(addressType),
  };
};

const ownerFilter = useFilter({
  entity: "user",
  options: {
    params: {
      roles: "kam",
      isActive: 1,
    },
  },
});

const agentFilter = useFilter({
  entity: "user",
  options: {
    params: {
      roles: "agent",
      isActive: 1,
    },
  },
});

const populateOwnerData = (owner) => {
  formData.value.owner_name =
    owner?.name ||
    unref(ownerFilter.entities).find(
      (owner) => owner._id === unref(formData).owner,
    ).name;
};

const {
  addItem,
  editItem,
  calculateTotalTax,
  hasItemsWithZeroQtyOrRate,
  pagination,
  removeProduct,
} = useProductList({
  formData,
});

const validateNumber = async (value) => {
  try {
    validatingNumber.value = true;

    isDuplicatePO.value = false;

    const payload = {
      number: value,
      _id: unref(formData)?._id,
      buyer: unref(formData).buyer,
    };

    await isDuplicate(payload);

    validatingNumber.value = false;

    return true;
  } catch (error) {
    validatingNumber.value = false;

    isDuplicatePO.value = true;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      return "PO number already exists";
    }

    return true;
  }
};

const route = useRoute();

const { file, uploadFile } = useFile(formData);

const onSubmit = async () => {
  try {
    const submission = await submitForm(false);

    isLoading.value = true;

    if (unref(file)) {
      await uploadFile({
        ...submission?.data?.data,
        type: "Purchase Order",
        showMessage: false,
      });
    }

    if (route.name === "sales-purchase-orders-id") {
      const poID = unref(purchaseOrder)?._id;

      if (poID) {
        await getCounts();

        await getPurchaseOrderFiles();
      }
    }

    if (route.name === "sales-purchase-orders") {
      await getPurchaseOrders();
    }

    isLoading.value = false;

    closeForm(true);

    clearTempAddresses();

    notify({
      message: submission?.data?.message,
      type: "positive",
    });
  } catch (error) {
    console.error(error);
  }
};

let productListColumns = computed(() => {
  const columns = [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: "Qty",
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
    {
      align: "center",
      classes: "sticky--right",
      headerClasses: "sticky--right",
      name: "action",
      label: "Action",
    },
  ];

  return columns;
});

const submitBtn = computed(() => {
  return {
    loading: unref(isLoading),
    disable:
      unref(validatingNumber) ||
      unref(isDuplicatePO) ||
      Boolean(unref(activeBills)) ||
      !unref(formData).lineitems.length ||
      unref(hasItemsWithZeroQtyOrRate),
  };
});

const onUploaded = async (uploadedPo) => {
  const { buyer, line_items, date, number, delivery_date, bill_to, ship_to } =
    uploadedPo;

  populateBuyerData(buyer);

  formData.value = {
    ...unref(formData),
    buyer,
    date: isValidDateString(date) ? extractDate(date, "DD-MM-YYYY") : null,
    number,
    delivery_date: isValidDateString(delivery_date)
      ? extractDate(delivery_date, "DD-MM-YYYY")
      : null,
    lineitems: line_items,
  };

  await populateBuyerAddressDropdown();

  const matchedBillingAddress = unref(billingAddresses)?.find(
    (bAddress) =>
      bAddress?.state?.toLowerCase() == bill_to?.state?.toLowerCase(),
  );

  const matchedShippingAddress = unref(shippingAddresses)?.find(
    (sAddress) =>
      sAddress?.state?.toLowerCase() == ship_to?.state?.toLowerCase(),
  );

  if (matchedBillingAddress && matchedShippingAddress) {
    formData.value = {
      ...unref(formData),
      billing_address: matchedBillingAddress,
      shipping_address: matchedShippingAddress,
    };
  }

  if (matchedBillingAddress) {
    updateAddress(
      matchedBillingAddress,
      "billing_address",
      buyer?.gstin,
      buyer?.shop_name,
    );
  } else {
    temp_billing_address.value = "";

    formData.value = {
      ...unref(formData),
      billing_address: "",
    };
  }

  if (matchedShippingAddress) {
    updateAddress(
      matchedShippingAddress,
      "shipping_address",
      buyer?.gstin,
      buyer?.shop_name,
    );
  } else {
    temp_shipping_address.value = "";

    formData.value = {
      ...unref(formData),
      shipping_address: "",
    };
  }

  nextTick(() => {
    calculateTotalTax();
  });
};

const { toggleUploadForm } = usePurchaseOrderStore();

const computedPaymentTerms = computed({
  get: () => {
    return unref(formData).payment_terms;
  },
  set: (paymentTerms) => {
    unref(formData).payment_terms =
      (paymentTerms || "0") + (paymentTerms > 1 ? " days" : " day");
  },
});

const paymentTermsFilter = useFilter({
  entity: "paymentTerms",
});

const updateAddress = (info, address_type, gstin, shop_name) => {
  if (!info) {
    return;
  }

  unref(formData)[`${address_type}_info`] = {
    city: info.city,
    state: info.state,
    pincode: info.pincode,
    address_line: info.address_line,
    state_code: info.state_code,
    shop_name: shop_name,
    address_id: info._id,
  };

  const gstinText = gstin ? `\nGSTIN: ${gstin}` : "";
  const concatenatedAddress = `${info.address_line ?? ""}${info.city ? ", " + info.city : ""}${info.state ? ", " + info.state : ""}${info.pincode ? ", " + info.pincode : ""}${gstinText}`;

  unref(formData)[`${address_type}`] = concatenatedAddress;

  if (address_type === "billing_address") {
    temp_billing_address.value = `${shop_name}\n${concatenatedAddress}`;
  } else {
    temp_shipping_address.value = `${shop_name}\n${concatenatedAddress}`;
  }
};

const getAddressOptionLabel = (address, gstin) => {
  if (!address) return;
  else if (typeof address === "string") {
    return address;
  } else if (typeof address === "object") {
    const addressLine = address.address_line || address.address;

    return (
      `${addressLine ?? ""}${address.city ? ", " + address.city : ""}${address.state ? ", " + address.state : ""}${address.pincode ? ", " + address.pincode : ""}` +
      (gstin ? "\nGSTIN: " + gstin : "")
    );
  }
};

watchEffect(() => {
  if (unref(formState).edit && unref(formState).open) {
    populateBuyerAddressDropdown();

    const shop_name = unref(formData).buyer?.shop_name;
    const { billing_address, shipping_address } = unref(formData);

    if ((billing_address, shipping_address)) {
      temp_billing_address.value = `${shop_name}\n${billing_address}`;

      temp_shipping_address.value = `${shop_name}\n${shipping_address}`;
    }
  }
});

const clearTempAddresses = () => {
  temp_billing_address.value = "";

  temp_shipping_address.value = "";
};

const onClose = () => {
  closeForm();

  clearTempAddresses();
};

const onReset = () => {
  resetForm();

  clearTempAddresses();
};

const isPONumberRequired = computed(() => {
  return Boolean(unref(formData).buyer?.po_applicable);
});

const isPOFileMandatory = computed(() => {
  const is_po_file_mandatory = Boolean(
    unref(formData).buyer?.is_po_file_mandatory,
  );

  const po_file_applicable = Boolean(unref(formData).buyer?.po_file_applicable);

  return is_po_file_mandatory || po_file_applicable;
});
</script>
